import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, Loader2, RefreshCw, CreditCard, TrendingUp, 
  Users, Euro, ArrowUpRight, ArrowDownRight, ExternalLink,
  Wallet, Clock, CheckCircle, AlertCircle, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminStripeDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [balance, setBalance] = useState(null);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState(null);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [statsDays, setStatsDays] = useState(30);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchData = useCallback(async () => {
    try {
      const [balanceRes, statsRes, txnRes, accountsRes, dailyRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stripe/balance`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/stripe/revenue-stats?days=${statsDays}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/stripe/transactions?limit=10`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/stripe/connect-accounts`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/stripe/daily-revenue?days=14`, { headers: getAuthHeaders() })
      ]);

      if (balanceRes.ok) setBalance(await balanceRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (txnRes.ok) {
        const txnData = await txnRes.json();
        setTransactions(txnData.transactions || []);
      }
      if (accountsRes.ok) setConnectedAccounts(await accountsRes.json());
      if (dailyRes.ok) {
        const dailyData = await dailyRes.json();
        setDailyRevenue(dailyData.daily_revenue || []);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statsDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Simple bar chart component
  const RevenueChart = ({ data }) => {
    const maxValue = Math.max(...data.map(d => d.fees), 1);
    
    return (
      <div className="flex items-end gap-1 h-32">
        {data.map((day, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className="w-full bg-accent rounded-t transition-all hover:bg-accent/80"
              style={{ height: `${Math.max((day.fees / maxValue) * 100, 2)}%` }}
              title={`${day.date}: ${formatCurrency(day.fees)}`}
            />
            {i % 2 === 0 && (
              <span className="text-[10px] text-muted-foreground">
                {new Date(day.date).getDate()}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-8" data-testid="admin-stripe-dashboard">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-accent" />
                Stripe Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Revenus et paiements de la plateforme en temps réel
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Stripe Dashboard
            </Button>
          </div>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Available Balance */}
          <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Solde disponible</p>
                  <p className="text-3xl font-bold text-green-600">
                    {balance?.available?.[0] 
                      ? formatCurrency(balance.available[0].amount, balance.available[0].currency)
                      : '0,00 €'
                    }
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Balance */}
          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">En attente</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {balance?.pending?.[0] 
                      ? formatCurrency(balance.pending[0].amount, balance.pending[0].currency)
                      : '0,00 €'
                    }
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Platform Revenue */}
          <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Revenus ({statsDays}j)
                  </p>
                  <p className="text-3xl font-bold text-accent">
                    {formatCurrency(stats?.platform_revenue || 0)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Count */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Transactions ({statsDays}j)
                  </p>
                  <p className="text-3xl font-bold">
                    {stats?.transaction_count || 0}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-500/20 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Volume Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Volume total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold mb-2">
                {formatCurrency(stats?.total_volume || 0)}
              </p>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-green-600">
                  <ArrowUpRight className="w-4 h-4" />
                  Plateforme: {formatCurrency(stats?.platform_revenue || 0)}
                </span>
                <span className="flex items-center gap-1 text-blue-600">
                  <ArrowDownRight className="w-4 h-4" />
                  Prestataires: {formatCurrency(stats?.provider_payouts || 0)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Chart */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Revenus plateforme (14 derniers jours)</CardTitle>
            </CardHeader>
            <CardContent>
              {dailyRevenue.length > 0 ? (
                <RevenueChart data={dailyRevenue} />
              ) : (
                <div className="h-32 flex items-center justify-center text-muted-foreground">
                  Aucune donnée disponible
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Connected Accounts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Comptes connectés
                </span>
                <Badge variant="outline">
                  {connectedAccounts?.active_count || 0} / {connectedAccounts?.total || 0} actifs
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {connectedAccounts?.accounts?.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {connectedAccounts.accounts.map((account) => (
                    <div 
                      key={account.provider_id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{account.name}</p>
                        <p className="text-sm text-muted-foreground">{account.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {account.charges_enabled ? (
                          <Badge className="bg-green-500/10 text-green-600">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Actif
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            En attente
                          </Badge>
                        )}
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            {formatCurrency(account.balance?.available || 0)}
                          </p>
                          <p className="text-xs text-muted-foreground">disponible</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun compte connecté</p>
                  <p className="text-sm">Les prestataires doivent configurer Stripe Connect</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Euro className="w-5 h-5" />
                Dernières transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {transactions.map((txn) => (
                    <div 
                      key={txn.id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                    >
                      <div>
                        <p className="font-medium capitalize">{txn.type.replace('_', ' ')}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(txn.created)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${txn.net > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {txn.net > 0 ? '+' : ''}{formatCurrency(txn.net, txn.currency)}
                        </p>
                        {txn.fee > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Frais: {formatCurrency(txn.fee, txn.currency)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Euro className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune transaction récente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Mode Info */}
        {balance && (
          <div className="mt-6 text-center">
            <Badge variant={balance.livemode ? "default" : "secondary"} className="px-4">
              {balance.livemode ? '🟢 Mode Production' : '🟡 Mode Test'}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
}
